// src/redux/slices/propertySlice.js

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { propertyAPI, adminAPI } from '../../services/api';

// ========================================
// 🏠 PROPERTY ASYNC THUNKS
// ========================================
// ❌ CURRENT CODE (PROBLEMATIC)
// ✅ FIXED VERSION
const getImageUrl = (imageData) => {
  // ✅ fallback placeholder (from frontend /public/images/)
  if (!imageData) return '/images/property-placeholder.jpg';

  // ✅ if it's an object with `url` property
  if (typeof imageData === 'object' && imageData.url) {
    return getImageUrl(imageData.url); // recursively resolve
  }

  // ✅ if it's already a full URL (Cloudinary or backend)
  if (typeof imageData === 'string' && imageData.startsWith('http')) {
    return imageData;
  }

  // ✅ if it starts with /uploads/, prepend backend API URL
  if (typeof imageData === 'string' && imageData.startsWith('/uploads/')) {
    return `${process.env.REACT_APP_API_URL}${imageData}`;
  }

  // ✅ if it's just a filename, build backend path
  if (typeof imageData === 'string') {
    return `${process.env.REACT_APP_API_URL}/uploads/properties/${imageData}`;
  }

  // ✅ default fallback
  return '/images/property-placeholder.jpg';
};



export const fetchProperties = createAsyncThunk(
  'properties/fetchProperties',
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await propertyAPI.getAll(params); // ✅ Use public API - no token required
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch properties');
    }
  }
);

export const fetchFeaturedProperties = createAsyncThunk(
  'properties/fetchFeaturedProperties',
  async (limit = 6, { rejectWithValue }) => {
    try {
      const response = await propertyAPI.getFeatured(limit); // ✅ Use public API - no token required
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch featured properties');
    }
  }
);

export const fetchPropertyById = createAsyncThunk(
  'properties/fetchPropertyById',
  async (id, { rejectWithValue }) => {
    try {
      
      const response = await propertyAPI.getById(id);
      
      
      // ✅ CRITICAL: Return the nested data object
      return response.data; // This contains the actual property object
    } catch (error) {
      console.error('❌ Error fetching property:', error);
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch property');
    }
  }
);

export const createProperty = createAsyncThunk(
  'properties/createProperty',
  async (propertyData, { rejectWithValue }) => {
    try {
      const requiredFields = ['title', 'description', 'price', 'address', 'city', 'state', 'zipCode'];
      const missingFields = requiredFields.filter(field => !propertyData[field]?.toString().trim());
      
      if (missingFields.length > 0) {
        return rejectWithValue(`Missing required fields: ${missingFields.join(', ')}`);
      }
      
      const response = await adminAPI.createProperty(propertyData); // ✅ Admin API for protected operations
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.message ||
        error.response?.data?.errors?.[0]?.message ||
        error.message ||
        'Failed to create property';
      return rejectWithValue(errorMessage);
    }
  }
);

export const updateProperty = createAsyncThunk(
  'properties/updateProperty',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await adminAPI.updateProperty(id, data); // ✅ Admin API for protected operations
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update property');
    }
  }
);

export const deleteProperty = createAsyncThunk(
  'properties/deleteProperty',
  async (id, { rejectWithValue }) => {
    try {
      await adminAPI.deleteProperty(id); // ✅ Admin API for protected operations
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete property');
    }
  }
);

export const toggleFeatured = createAsyncThunk(
  'properties/toggleFeatured',
  async (id, { rejectWithValue }) => {
    try {
      const response = await adminAPI.togglePropertyFeatured(id);
      return { id, ...response };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to toggle featured');
    }
  }
);

// ========================================
// 📊 INITIAL STATE
// ========================================

const initialState = {
  properties: [],
  featuredProperties: [],
  currentProperty: null,
  loading: false,
  featuredLoading: false,
  operationLoading: false,
  analyticsLoading: false,
  pagination: {
    currentPage: 1,
    totalPages: 1,
    totalProperties: 0,
    limit: 10,
    hasNext: false,
    hasPrev: false
  },
  filters: {
    search: '',
    propertyType: '',
    status: '',
    minPrice: '',
    maxPrice: '',
    bedrooms: '',
    bathrooms: '',
    featured: '',
    sortBy: 'createdAt',
    sortOrder: 'DESC',
    page: 1
  },
  analytics: null,
  error: null,
  success: null,
  lastUpdated: null
};

// ========================================
// 🧬 PROPERTY SLICE
// ========================================

const propertySlice = createSlice({
  name: 'properties',
  initialState,
  reducers: {
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearFilters: (state) => {
      state.filters = initialState.filters;
    },
    updateFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    resetFilters: (state) => {
      state.filters = initialState.filters;
    },
    clearError: (state) => {
      state.error = null;
    },
    clearSuccess: (state) => {
      state.success = null;
    },
    setCurrentProperty: (state, action) => {
      state.currentProperty = action.payload;
    },
    clearCurrentProperty: (state) => {
      state.currentProperty = null;
      state.error = null;
    },
    updatePropertyLocally: (state, action) => {
      const { id, updates } = action.payload;
      const propertyIndex = state.properties.findIndex(prop => prop.id === id);
      if (propertyIndex !== -1) {
        state.properties[propertyIndex] = {
          ...state.properties[propertyIndex],
          ...updates,
          updatedAt: new Date().toISOString()
        };
      }
      if (state.currentProperty?.id === id) {
        state.currentProperty = { ...state.currentProperty, ...updates };
      }
    }
  },
  extraReducers: (builder) => {
    builder
      // ========================================
      // FETCH PROPERTIES
      // ========================================
      .addCase(fetchProperties.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      // propertySlice.js - Fix the fulfilled case
.addCase(fetchProperties.fulfilled, (state, action) => {
  state.loading = false;
  const rawProperties = action.payload.data?.properties || action.payload.properties || [];

  // ✅ Transform properties to extract proper image URLs
  state.properties = rawProperties.map(property => ({
    ...property,
    // ✅ Extract string URL from image object
    imageUrl: getImageUrl(property.featuredImage || property.coverImage || property.image || (property.images && property.images[0])),
    // ✅ Normalize images array - handle both string and object formats
    images: property.images?.map(img => {
      // If it's already a proper object with url, keep it
      if (typeof img === 'object' && img.url) {
        return {
          url: getImageUrl(img.url),
          alt: img.alt || 'Property image'
        };
      }
      // If it's a string, convert to object
      return {
        url: getImageUrl(img),
        alt: 'Property image'
      };
    }) || []
  }));

  state.pagination = action.payload.data?.pagination || action.payload.pagination || state.pagination;
  state.error = null;
})

      .addCase(fetchProperties.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // ========================================
      // FETCH FEATURED PROPERTIES
      // ========================================
      .addCase(fetchFeaturedProperties.pending, (state) => {
        state.featuredLoading = true;
        state.error = null;
      })
      .addCase(fetchFeaturedProperties.fulfilled, (state, action) => {
  state.featuredLoading = false;

  // normalize so it's always an array
  let raw = action.payload;

  if (raw?.data) {
    raw = raw.data;
  } 

  state.featuredProperties = Array.isArray(raw) ? raw : [];
})

      .addCase(fetchFeaturedProperties.rejected, (state, action) => {
        state.featuredLoading = false;
        state.error = action.payload;
      })

      // ========================================
      // FETCH PROPERTY BY ID
      // ========================================
      .addCase(fetchPropertyById.pending, (state) => {
        state.operationLoading = true;
        state.error = null;
      })
      .addCase(fetchPropertyById.fulfilled, (state, action) => {
  state.operationLoading = false;
  // ✅ CRITICAL: action.payload is now the property object itself
  state.currentProperty = action.payload;
  state.error = null;
  
})
      .addCase(fetchPropertyById.rejected, (state, action) => {
        state.operationLoading = false;
        state.error = action.payload;
        state.currentProperty = null;
      })

      // ========================================
      // CREATE PROPERTY
      // ========================================
      .addCase(createProperty.pending, (state) => {
        state.operationLoading = true;
        state.error = null;
      })
      .addCase(createProperty.fulfilled, (state, action) => {
        state.operationLoading = false;
        state.properties.unshift(action.payload);
        state.success = 'Property created successfully';
        state.lastUpdated = new Date().toISOString();
      })
      .addCase(createProperty.rejected, (state, action) => {
        state.operationLoading = false;
        state.error = action.payload;
      })

      // ========================================
      // UPDATE PROPERTY
      // ========================================
      .addCase(updateProperty.pending, (state) => {
        state.operationLoading = true;
        state.error = null;
      })
      .addCase(updateProperty.fulfilled, (state, action) => {
        state.operationLoading = false;
        const propertyIndex = state.properties.findIndex(prop => prop.id === action.payload.id);
        if (propertyIndex !== -1) {
          state.properties[propertyIndex] = action.payload;
        }
        if (state.currentProperty?.id === action.payload.id) {
          state.currentProperty = action.payload;
        }
        state.success = 'Property updated successfully';
        state.lastUpdated = new Date().toISOString();
      })
      .addCase(updateProperty.rejected, (state, action) => {
        state.operationLoading = false;
        state.error = action.payload;
      })

      // ========================================
      // DELETE PROPERTY
      // ========================================
      .addCase(deleteProperty.pending, (state) => {
        state.operationLoading = true;
        state.error = null;
      })
      .addCase(deleteProperty.fulfilled, (state, action) => {
        state.operationLoading = false;
        state.properties = state.properties.filter(prop => prop.id !== action.payload);
        if (state.currentProperty?.id === action.payload) {
          state.currentProperty = null;
        }
        state.success = 'Property deleted successfully';
        state.lastUpdated = new Date().toISOString();
      })
      .addCase(deleteProperty.rejected, (state, action) => {
        state.operationLoading = false;
        state.error = action.payload;
      })

      // ========================================
      // TOGGLE FEATURED
      // ========================================
      .addCase(toggleFeatured.fulfilled, (state, action) => {
        const { id, featured } = action.payload;
        const property = state.properties.find(prop => prop.id === id);
        if (property) {
          property.featured = featured;
        }
        if (state.currentProperty?.id === id) {
          state.currentProperty.featured = featured;
        }
        state.success = `Property ${featured ? 'marked as featured' : 'removed from featured'}`;
      });
  }
});


// ========================================
// 🎯 ACTIONS EXPORT
// ========================================

export const {
  setFilters,
  clearFilters,
  updateFilters,
  resetFilters,
  clearError,
  clearSuccess,
  setCurrentProperty,
  clearCurrentProperty,
  updatePropertyLocally
} = propertySlice.actions;

// ========================================
// 🎯 SELECTORS EXPORT
// ========================================

export const selectProperties = (state) => state.properties.properties;
export const selectFeaturedProperties = (state) => state.properties.featuredProperties;
export const selectCurrentProperty = (state) => state.properties.currentProperty;
export const selectPropertiesLoading = (state) => state.properties.loading;
export const selectPropertyLoading = (state) => state.properties.loading;
export const selectFeaturedLoading = (state) => state.properties.featuredLoading;
export const selectOperationLoading = (state) => state.properties.operationLoading;
export const selectCurrentPropertyLoading = (state) => state.properties.operationLoading;
export const selectPagination = (state) => state.properties.pagination;
export const selectPropertyPagination = (state) => state.properties.pagination;
export const selectFilters = (state) => state.properties.filters;
export const selectPropertyFilters = (state) => state.properties.filters;
export const selectAnalytics = (state) => state.properties.analytics;
export const selectError = (state) => state.properties.error;
export const selectPropertyError = (state) => state.properties.error;
export const selectSuccess = (state) => state.properties.success;

export default propertySlice.reducer;
